import { addDays, getDayOfYear, isAfter, sub } from 'date-fns';
import { Appointment } from 'domains/appointments';
import { MAX_RESCHEDULE_DAYS_RANGE } from 'domains/appointments/reschedule/calendar';
import { useUserClientsWithActiveClient } from 'domains/auth/UserClientsWrapper/useUserClients';
import {
  getRescheduleAvailableTimes,
  getScheduleAvailableTimesV4,
  getConsultationAvailableTimes,
  getCalendarAvailability,
} from 'domains/calendars/queries';
import { isUndefined } from 'lodash';
import { useInfiniteQuery, useQuery } from 'react-query';
import { StateAbbrev } from 'utils/states';

export const useRescheduleAvailableTimes = (appointment?: Appointment) => {
  const { activeClientId, contactId } = useUserClientsWithActiveClient();

  return useInfiniteQuery(
    ['reschedule-available-times', activeClientId, contactId, appointment?.acuityID],
    ({ pageParam }) => {
      if (!appointment) {
        throw new Error(`Expected appointment to be defined`);
      }
      const today = new Date();
      const minRescheduleDate = sub(appointment.appointmentDateTime, { days: MAX_RESCHEDULE_DAYS_RANGE });
      const queryDatesFrom = isAfter(minRescheduleDate, today) ? minRescheduleDate : today;

      return getRescheduleAvailableTimes(activeClientId, appointment.acuityID, pageParam ?? queryDatesFrom);
    },
    {
      retry: false,
      enabled: !isUndefined(appointment),
    },
  );
};

export type UseEvaluationAvailableTimesParams = {
  state: StateAbbrev;
  from: Date;
  daysOffset?: number;
  insuranceName?: string;
  detectedAreas: string[];
};

export const useEvaluationAvailableTimes = ({
  detectedAreas,
  from,
  insuranceName,
  state,
  daysOffset,
}: UseEvaluationAvailableTimesParams) => {
  const queryFrom = addDays(from, daysOffset ?? 0);
  return useQuery(
    ['evaluation-timeSlots', getDayOfYear(queryFrom), detectedAreas, insuranceName, state],
    async () => {
      const data = await getScheduleAvailableTimesV4({
        date: queryFrom,
        insuranceName,
        state,
        specialties: detectedAreas.join(','),
      });

      return data.availableTimes;
    },

    {
      retry: false,
      enabled: !isUndefined(daysOffset),
    },
  );
};

export const useConsultationAvailableTimes = (from: Date, calendarID?: string) => {
  return useQuery(
    ['consultation-timeSlots', getDayOfYear(from)],
    async () =>
      getConsultationAvailableTimes({
        appointmentType: 'initial-consult',
        calendarID,
      }),
    {
      retry: false,
    },
  );
};

interface ICalendarAvailableTimes {
  calendarID: string;
  date: string;
}

export const useCalendarAvailableTimes = ({
  calendarID,
  date,
}: ICalendarAvailableTimes) => {
  return useQuery(
    ['calendar-availability', calendarID, date],
    async () => {
      const data = await getCalendarAvailability({
        date,
        calendarID,
      });

      return data;
    },
    {
      retry: false,
      enabled: !!calendarID && !!date,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
    },
  );
};
